import { useQuery } from '@apollo/client';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import Table from '@risksmart-app/components/src/table';
import { GetActiveAppetitesByParentIdDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';
import ImpactRatingModal from 'src/pages/impacts/ratings/ImpactRatingModal';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteImpactRatings } from '@/hooks/mutations/impact-rating/useDeleteImpactRatings';
import { useInsertChildImpactRatings } from '@/hooks/mutations/impact-rating/useInsertChildImpactRatings';
import { useGetImpactRatingsByRatedItemId } from '@/hooks/queries/impact-rating/useGetImpactRatingsByRatedItemId';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import InsertMultipleImpactRatingsModal from '../../../../impacts/ratings/InsertMultipleImpactRatingsModal';
import { useGetCollectionTableProps } from './config';
import type { ImpactRatingTableFields } from './types';

interface Props {
  parentRisk: ObjectWithContributors;
}

const Tab: FC<Props> = ({ parentRisk }) => {
  useI18NSummaryHelpContent('impactRatings.tabHelp');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'impactRatings',
  });
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isEditMultipleOpen, setIsEditMultipleOpen] = useState(false);
  const [ratingId, setRatingId] = useState<string | undefined>(undefined);
  const [selectedRatings, setSelectedRatings] = useState<
    ImpactRatingTableFields[]
  >([]);

  const { hasPermission: canDeleteRatings, loading: canDeleteRatingsLoading } =
    useHasPermissionQuery('delete:impact_rating', parentRisk);

  const { addNotification } = useNotifications();
  const { data, loading, refetch } = useGetImpactRatingsByRatedItemId({
    queryArgs: { ratedItemId: parentRisk.Id },
  });

  const { data: appetiteData, loading: loadingAppetites } = useQuery(
    GetActiveAppetitesByParentIdDocument,
    {
      variables: {
        parentId: parentRisk.Id,
      },
      onError: (error) => {
        addNotification({
          type: 'error',
          content: <>{error.message}</>,
        });
      },
    }
  );

  const { deleteImpactRatings: deleteRatings, loading: deleteLoading } =
    useDeleteImpactRatings();

  const handleRatingsOpen = () => {
    setIsEditMultipleOpen(true);
  };

  const handleRatingCreateClose = () => {
    setRatingId(undefined);
    setIsEditOpen(false);
  };

  const handleMultipleRatingsCreateClose = () => {
    setRatingId(undefined);
    setIsEditMultipleOpen(false);
  };

  const { insertChildImpactRatings: insertRatings } =
    useInsertChildImpactRatings();

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await deleteRatings({
        Ids: selectedRatings.map((s) => s.Id),
      });
      await refetch();
      setSelectedRatings([]);
      setIsDeleteModalVisible(false);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const tableProps = useGetCollectionTableProps(
    parentRisk,
    data,
    appetiteData?.appetite_parent.map((a) => a.appetite),
    ({ Id }) => {
      setRatingId(Id);
      setIsEditOpen(true);
    },
    handleRatingsOpen
  );

  return (
    <>
      <Table
        {...tableProps}
        selectionType={
          canDeleteRatings && !canDeleteRatingsLoading ? 'multi' : undefined
        }
        selectedItems={selectedRatings}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedRatings(detail.selectedItems);
        }}
        resizableColumns={true}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Permission
                    permission={'delete:impact_rating'}
                    parentObject={parentRisk}
                  >
                    <Button
                      formAction={'none'}
                      variant={'normal'}
                      disabled={!selectedRatings.length}
                      onClick={() => setIsDeleteModalVisible(true)}
                    >
                      {t('delete')}
                    </Button>
                  </Permission>
                  <Permission
                    permission={'insert:impact_rating'}
                    parentObject={parentRisk}
                  >
                    <Button
                      variant={'primary'}
                      formAction={'none'}
                      onClick={handleRatingsOpen}
                    >
                      {t('impactRatingsMultiple.create_new_button')}
                    </Button>
                  </Permission>
                </SpaceBetween>
              }
            >
              {st('tab_title')}
            </TabHeader>
          </SpaceBetween>
        }
        variant={'embedded'}
        loading={loading || loadingAppetites}
        sortingDisabled={false}
      />
      {isEditOpen && (
        <ImpactRatingModal
          ratedItemId={parentRisk.Id}
          impactRatingId={ratingId}
          onSaving={async (data) => {
            await insertRatings({
              Ratings: [
                {
                  ImpactId: data.ImpactId,
                  Rating: data.Rating,
                },
              ],
              TestDate: data.TestDate,
              RatedItemId: data.RatedItemId,
              CustomAttributeData: data.CustomAttributeData ?? null,
              CompletedBy: data.CompletedBy?.value,
              Likelihood: data.Likelihood,
            });
            await refetch();
          }}
          onDismiss={handleRatingCreateClose}
        />
      )}
      {isEditMultipleOpen && (
        <InsertMultipleImpactRatingsModal
          impactRatingId={ratingId}
          onSaving={async (data) => {
            await insertRatings({
              Ratings: data.Ratings,
              TestDate: data.TestDate,
              RatedItemId: parentRisk.Id,
              CustomAttributeData: data.CustomAttributeData ?? null,
              CompletedBy: data.CompletedBy?.value,
              Likelihood: data.Likelihood,
            });
            await refetch();
          }}
          onDismiss={handleMultipleRatingsCreateClose}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </>
  );
};

export default Tab;
